/**
 * Normalized market-data contracts shared by the client and server.
 *
 * These types intentionally describe our own domain model rather than any
 * specific provider's response shape. The server maps the upstream provider
 * (currently Alpaca) into these structures so the UI never needs to know which
 * data source is in use.
 */

/** A single OHLCV bar (candle). Timestamps are UTC epoch seconds. */
export interface Bar {
  /** UTC epoch seconds at the start of the bar. */
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

/** How stale the returned data is, so the UI can be honest with users. */
export type DataSource = "sip" | "iex" | "delayed" | "realtime";

/** A tradable instrument the user can chart. */
export interface Asset {
  symbol: string;
  name: string;
  exchange: string;
  /** "us_equity" for stocks/ETFs. */
  class: string;
  tradable: boolean;
}

/** Historical bars for a symbol over a selected range. */
export interface BarsResponse {
  symbol: string;
  range: RangeKey;
  interval: BarInterval;
  bars: Bar[];
  /** Which upstream feed produced these bars. */
  source: DataSource;
  /** True when the most recent bar may be delayed (free plan). */
  delayed: boolean;
  /** UTC epoch ms when the server produced this response. */
  asOf: number;
  /** Epoch seconds to request for the preceding page, or null at data limit. */
  nextBefore: number | null;
}

/** A lightweight, frequently-refreshed price quote for a symbol. */
export interface Quote {
  symbol: string;
  /** Latest known trade price. */
  price: number;
  /** Previous session close, used to compute day change. */
  previousClose: number;
  /** Absolute change vs. previous close. */
  change: number;
  /** Percentage change vs. previous close. */
  changePercent: number;
  /** Volume for the current/most-recent session. */
  volume: number;
  source: DataSource;
  delayed: boolean;
  /** UTC epoch ms when the underlying trade occurred, if known. */
  timestamp: number;
}

/** Batched quote lookup response. */
export interface QuotesResponse {
  quotes: Record<string, Quote>;
  asOf: number;
}

/** Whether the US market is currently open. */
export interface MarketStatus {
  isOpen: boolean;
  /** ISO timestamp of the next open, if known. */
  nextOpen: string | null;
  /** ISO timestamp of the next close, if known. */
  nextClose: string | null;
  /** Server time (ISO) the status was computed. */
  serverTime: string;
}

/** Symbol search result payload. */
export interface SearchResponse {
  query: string;
  results: Asset[];
}

/** A structured API error the client can render nicely. */
export interface ApiError {
  error: {
    code: ApiErrorCode;
    message: string;
  };
}

export type ApiErrorCode =
  | "not_configured"
  | "rate_limited"
  | "not_found"
  | "bad_request"
  | "upstream_error"
  | "internal_error";

/** Supported time ranges the user can select per chart. */
export type RangeKey =
  | "1D"
  | "5D"
  | "1M"
  | "3M"
  | "6M"
  | "YTD"
  | "1Y"
  | "5Y"
  | "MAX";

/** Bar aggregation intervals we request from the provider. */
export type BarInterval =
  | "1Min"
  | "5Min"
  | "15Min"
  | "30Min"
  | "1Hour"
  | "1Day"
  | "1Week"
  | "1Month";

/** Chart rendering styles offered in the UI. */
export type ChartType = "candlestick" | "line" | "area" | "bar";

export const RANGE_KEYS: readonly RangeKey[] = [
  "1D",
  "5D",
  "1M",
  "3M",
  "6M",
  "YTD",
  "1Y",
  "5Y",
  "MAX",
] as const;

export const CHART_TYPES: readonly ChartType[] = [
  "candlestick",
  "line",
  "area",
  "bar",
] as const;
